import { Button } from "@d12labs/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@d12labs/ui/card";
import {
	type ChartConfig,
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from "@d12labs/ui/chart";
import { EmptyState } from "@d12labs/ui/empty-state";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Quote, Sparkles, Target } from "lucide-react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { meetingPerfApi } from "../api";
import { Delta } from "../components/delta";
import { CATEGORY_LABELS, type CategoryKey } from "../types";

// ── Types (narrow the server coaching rows) ─────────────────────────────────
// The session's `trendSnapshot`/`evidence` are opaque jsonb (`Record<string,
// unknown>`) on the server row — the synthesis slice owns their shape via its
// Zod schema — and a focus row's `dimension` is an untyped text column CHECK-
// constrained to the six CategoryKey values. The page derives the rows from
// the route defs and narrows exactly those fields to what it renders.

type DimensionTrend = { avg: number; slope: number };
type Trendlines = {
	global: Record<string, DimensionTrend>;
	bySegment: Record<string, Record<string, DimensionTrend>>;
};
type CoachEvidence = {
	excerpt: string;
	meeting_title: string;
	timestamp_sec: number | null;
	why_it_matters: string;
};
type CoachingSessionRow = NonNullable<Awaited<ReturnType<typeof meetingPerfApi.coachLatest>>>;
type CoachingSession = Omit<CoachingSessionRow, "trendSnapshot" | "evidence"> & {
	trendSnapshot: {
		trendlines: Trendlines;
		trend: {
			focus_dimension: CategoryKey;
			headline: string;
			segment_callouts: { segment: string; note: string }[];
		};
	};
	evidence: { items: CoachEvidence[] };
};
type FocusRow = NonNullable<Awaited<ReturnType<typeof meetingPerfApi.focusActive>>>;
type ActiveFocus = (Omit<FocusRow, "dimension"> & { dimension: CategoryKey }) | null;

// ── Helpers ──────────────────────────────────────────────────────────────────

/** "client_pitch" → "Client pitch". */
function segmentLabel(segment: string): string {
	const s = segment.replace(/_/g, " ");
	return s.charAt(0).toUpperCase() + s.slice(1);
}

const chartConfig: ChartConfig = { avg: { label: "Avg score", color: "var(--color-chart-1)" } };

/** Per-context average (×10 → 0–100) for the highlighted dimension. */
function buildChartData(trendlines: Trendlines, dimension: CategoryKey) {
	const rows: { context: string; avg: number; slope: number }[] = [];
	const g = trendlines.global[dimension];
	if (g) rows.push({ context: "Overall", avg: g.avg * 10, slope: g.slope });
	for (const [segment, dims] of Object.entries(trendlines.bySegment)) {
		const d = dims[dimension];
		if (d) rows.push({ context: segmentLabel(segment), avg: d.avg * 10, slope: d.slope });
	}
	return rows;
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function CoachingPage() {
	const qc = useQueryClient();

	const sessionQuery = useQuery<CoachingSession | null>({
		queryKey: ["meeting-perf", "coach", "latest"],
		queryFn: () => meetingPerfApi.coachLatest() as Promise<CoachingSession | null>,
	});
	const focusQuery = useQuery<ActiveFocus>({
		queryKey: ["meeting-perf", "coach", "focus"],
		queryFn: () => meetingPerfApi.focusActive() as Promise<ActiveFocus>,
	});

	const runSession = useMutation({
		mutationFn: () => meetingPerfApi.coachSession(),
		onSuccess: () => {
			toast.success("Coaching session ready");
			qc.invalidateQueries({ queryKey: ["meeting-perf", "coach"] });
		},
		onError: (err) => toast.error((err as Error).message),
	});

	const markEngaged = useMutation({
		mutationFn: (focusId: string) => meetingPerfApi.focusEngaged({ params: { id: focusId } }),
		onSuccess: () => {
			toast.success("Nice — keep it up");
			qc.invalidateQueries({ queryKey: ["meeting-perf", "coach", "focus"] });
		},
		onError: (err) => toast.error((err as Error).message),
	});

	const session = sessionQuery.data;
	const focus = focusQuery.data;

	return (
		<>
			<PageHeader
				title="Coaching"
				subtitle="Where you're trending, what happened, and your one thing this week."
				actions={
					<Button onClick={() => runSession.mutate()} disabled={runSession.isPending}>
						<Sparkles className="h-4 w-4" />
						{runSession.isPending ? "Running…" : session ? "Run new session" : "Run a session"}
					</Button>
				}
			/>
			<PageBody>
				{!session ? (
					<EmptyState
						icon={Sparkles}
						title="No coaching session yet"
						body="Run a session to get a focused read on your recent meetings: where you're trending, a few grounded moments, and one thing to work on."
					/>
				) : (
					<div className="flex flex-col gap-6">
						<TrendCard session={session} />
						<EvidenceCard evidence={session.evidence.items} />
						<OneThingCard
							session={session}
							focus={focus ?? null}
							onEngaged={(id) => markEngaged.mutate(id)}
							engaging={markEngaged.isPending}
						/>
					</div>
				)}
			</PageBody>
		</>
	);
}

function TrendCard({ session }: { session: CoachingSession }) {
	const dim = session.trendSnapshot.trend.focus_dimension;
	const data = buildChartData(session.trendSnapshot.trendlines, dim);
	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					Trend — {CATEGORY_LABELS[dim] ?? dim}
				</CardTitle>
				<CardDescription>{session.trendSnapshot.trend.headline}</CardDescription>
			</CardHeader>
			<CardContent className="flex flex-col gap-4">
				<ChartContainer config={chartConfig} className="aspect-[3/1] w-full">
					<BarChart data={data} margin={{ left: 4, right: 12, top: 8, bottom: 4 }}>
						<CartesianGrid vertical={false} />
						<XAxis dataKey="context" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
						<YAxis
							domain={[0, 100]}
							width={28}
							tickLine={false}
							axisLine={false}
							tick={{ fontSize: 11 }}
						/>
						<ChartTooltip content={<ChartTooltipContent />} />
						<Bar dataKey="avg" fill="var(--color-avg)" radius={4} isAnimationActive={false} />
					</BarChart>
				</ChartContainer>
				<ul className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
					{data.map((row) => (
						<li key={row.context} className="flex items-center gap-2">
							<span className="text-muted-foreground">{row.context}</span>
							<Delta value={row.slope} />
						</li>
					))}
				</ul>
			</CardContent>
		</Card>
	);
}

function EvidenceCard({ evidence }: { evidence: CoachEvidence[] }) {
	if (evidence.length === 0) return null;
	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					<Quote className="h-4 w-4" /> What happened
				</CardTitle>
				<CardDescription>Grounded moments from your recent meetings.</CardDescription>
			</CardHeader>
			<CardContent className="flex flex-col gap-4">
				{evidence.map((e) => (
					<blockquote
						key={`${e.meeting_title}:${e.timestamp_sec}:${e.excerpt}`}
						className="border-l-2 border-border pl-4"
					>
						<p className="text-sm">“{e.excerpt}”</p>
						<footer className="mt-1 text-xs text-muted-foreground">
							{e.meeting_title}
							{e.timestamp_sec != null
								? ` · ${Math.floor(e.timestamp_sec / 60)}:${String(e.timestamp_sec % 60).padStart(2, "0")}`
								: ""}
						</footer>
						<p className="mt-1 text-sm text-muted-foreground">{e.why_it_matters}</p>
					</blockquote>
				))}
			</CardContent>
		</Card>
	);
}

function OneThingCard({
	session,
	focus,
	onEngaged,
	engaging,
}: {
	session: CoachingSession;
	focus: ActiveFocus;
	onEngaged: (focusId: string) => void;
	engaging: boolean;
}) {
	// Prefer the live active focus (carries engaged state + id); fall back to the session's focus id.
	const focusId = focus?.id ?? session.focusAreaId;
	const dimension = focus?.dimension ?? session.trendSnapshot.trend.focus_dimension;
	const microAction = focus?.microAction ?? "";
	const engaged = focus?.engaged ?? false;
	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					<Target className="h-4 w-4" /> Your one thing
				</CardTitle>
				<CardDescription>{CATEGORY_LABELS[dimension] ?? dimension}</CardDescription>
			</CardHeader>
			<CardContent className="flex flex-col gap-4">
				{microAction ? <p className="text-sm">{microAction}</p> : null}
				<div>
					<Button
						variant={engaged ? "outline" : "default"}
						disabled={engaged || engaging}
						onClick={() => onEngaged(focusId)}
					>
						{engaged ? "Working on it ✓" : "I worked on this"}
					</Button>
				</div>
			</CardContent>
		</Card>
	);
}
